import {
  useVaultPermissions,
  useGrantPermission,
  useRevokePermission,
} from '@/hooks/useVault';
import { PermissionRole } from '@/types/vault.types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckCircle,
  Plus,
  Shield,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const grantPermissionSchema = z.object({
  userId: z.string().uuid('vault.permissions.validation.userId'),
  role: z.nativeEnum(PermissionRole),
  canRead: z.boolean().default(true),
  canDownload: z.boolean().default(false),
  canUpload: z.boolean().default(false),
  canDelete: z.boolean().default(false),
  canShare: z.boolean().default(false),
});

type GrantPermissionFormData = z.infer<typeof grantPermissionSchema>;

interface PermissionsTabProps {
  vaultId: string;
}

export const PermissionsTab = ({ vaultId }: PermissionsTabProps) => {
  const { t } = useTranslation();
  const [showGrantModal, setShowGrantModal] = useState(false);

  const { data: permissions, isLoading } = useVaultPermissions(vaultId);
  const revokePermission = useRevokePermission(vaultId);

  const handleRevoke = (permissionId: string) => {
    if (
      window.confirm(
        t(
          'vault.permissions.revokeConfirm',
          'Êtes-vous sûr de vouloir révoquer cette permission ?',
        ),
      )
    ) {
      revokePermission.mutate(permissionId);
    }
  };

  const getRoleBadgeColor = (role: PermissionRole) => {
    switch (role) {
      case PermissionRole.OWNER:
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case PermissionRole.CREATOR:
        return 'bg-green-100 text-green-700 border-green-200';
      case PermissionRole.ADMIN:
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case PermissionRole.CONTRIBUTOR:
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case PermissionRole.VIEWER:
        return 'bg-neutral-100 text-neutral-700 border-neutral-200';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-200';
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
            <Shield className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {t('vault.permissions.title', 'Permissions')}
            </h2>
            <p className="text-sm text-neutral-500">
              {t(
                'vault.permissions.description',
                'Gérez les accès au coffre-fort',
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowGrantModal(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
        >
          <UserPlus className="h-4 w-4" />
          {t('vault.permissions.grant', 'Accorder une permission')}
        </button>
      </div>

      {/* Permissions List */}
      {permissions && permissions.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-neutral-200">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.permissions.user', 'Utilisateur')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.permissions.role', 'Rôle')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.permissions.permissions', 'Droits')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.permissions.grantedAt', 'Accordée le')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('common.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 bg-white">
              {permissions.map((permission) => (
                <tr key={permission.uuid} className="hover:bg-neutral-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-600">
                        {permission.user?.email?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-neutral-900">
                          {permission.user?.email || t('common.unknown', 'Inconnu')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getRoleBadgeColor(
                        permission.role,
                      )}`}
                    >
                      {permission.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2">
                      {/* Les capacités sont déterminées par le rôle dans le nouveau modèle */}
                      {(permission.role === PermissionRole.OWNER ||
                        permission.role === PermissionRole.ADMIN) && (
                        <>
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-xs text-neutral-600">
                            <CheckCircle className="h-3 w-3" />
                            {t('vault.permissions.read', 'Lire')}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-xs text-neutral-600">
                            <CheckCircle className="h-3 w-3" />
                            {t('vault.permissions.download', 'Télécharger')}
                          </span>
                        </>
                      )}
                      {permission.role === PermissionRole.CREATOR && (
                        <>
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            {t('vault.permissions.viewHistory', 'Historique')}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            {t('vault.permissions.configSync', 'Config Sync')}
                          </span>
                        </>
                      )}
                      {permission.role === PermissionRole.VIEWER && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-xs text-neutral-600">
                          <CheckCircle className="h-3 w-3" />
                          {t('vault.permissions.read', 'Lire')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-neutral-500">
                    {new Date(permission.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {permission.role !== PermissionRole.OWNER && (
                        <button
                          onClick={() => handleRevoke(permission.uuid)}
                          className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('vault.permissions.revoke', 'Révoquer')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-12 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-200">
            <Shield className="h-8 w-8 text-neutral-500" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('vault.permissions.empty', 'Aucune permission')}
          </h3>
          <p className="mb-4 text-sm text-neutral-600">
            {t(
              'vault.permissions.emptyDescription',
              'Accordez des permissions pour partager ce coffre',
            )}
          </p>
          <button
            onClick={() => setShowGrantModal(true)}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            {t('vault.permissions.grantFirst', 'Accorder la première permission')}
          </button>
        </div>
      )}

      {/* Grant Permission Modal */}
      {showGrantModal && (
        <GrantPermissionModal
          vaultId={vaultId}
          isOpen={showGrantModal}
          onClose={() => setShowGrantModal(false)}
        />
      )}
    </div>
  );
};

// Grant Permission Modal Component
interface GrantPermissionModalProps {
  vaultId: string;
  isOpen: boolean;
  onClose: () => void;
}

const GrantPermissionModal = ({
  vaultId,
  isOpen,
  onClose,
}: GrantPermissionModalProps) => {
  const { t } = useTranslation();
  const grantPermission = useGrantPermission(vaultId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<GrantPermissionFormData>({
    resolver: zodResolver(grantPermissionSchema),
    defaultValues: {
      role: PermissionRole.VIEWER,
      canRead: true,
      canDownload: false,
      canUpload: false,
      canDelete: false,
      canShare: false,
    },
  });

  const onSubmit = async (data: GrantPermissionFormData) => {
    try {
      await grantPermission.mutateAsync(data);
      reset();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <UserPlus className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t('vault.permissions.grantTitle', 'Accorder une Permission')}
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            {t(
              'vault.permissions.grantDescription',
              'Donnez accès au coffre-fort à un utilisateur',
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* User ID */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.permissions.userIdLabel', 'ID Utilisateur')}
              <span className="text-error-600">*</span>
            </label>
            <input
              {...register('userId')}
              type="text"
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            {errors.userId && (
              <p className="mt-1 text-sm text-error-600">
                {t(errors.userId.message as string)}
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.permissions.roleLabel', 'Rôle')}
              <span className="text-error-600">*</span>
            </label>
            <select
              {...register('role')}
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value={PermissionRole.VIEWER}>
                {PermissionRole.VIEWER} - Lecture seule
              </option>
              <option value={PermissionRole.CONTRIBUTOR}>
                {PermissionRole.CONTRIBUTOR} - Contributeur
              </option>
              <option value={PermissionRole.CREATOR}>
                {PermissionRole.CREATOR} - Créateur (historique + config sync)
              </option>
              <option value={PermissionRole.ADMIN}>
                {PermissionRole.ADMIN} - Administrateur
              </option>
            </select>
          </div>

          {/* Permissions Checkboxes */}
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <p className="mb-3 text-sm font-semibold text-neutral-700">
              {t('vault.permissions.granularTitle', 'Permissions Détaillées')}
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  {...register('canRead')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">
                  {t('vault.permissions.canRead', 'Peut lire')}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  {...register('canDownload')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">
                  {t('vault.permissions.canDownload', 'Peut télécharger')}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  {...register('canUpload')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">
                  {t('vault.permissions.canUpload', 'Peut uploader')}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  {...register('canDelete')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">
                  {t('vault.permissions.canDelete', 'Peut supprimer')}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  {...register('canShare')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-700">
                  {t('vault.permissions.canShare', 'Peut partager')}
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {isSubmitting
                ? t('common.granting', 'Accord...')
                : t('vault.permissions.grantButton', 'Accorder')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
